import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { setTheme } from '../../store/slices/themeSlice';
import { ThemeId } from '../../types/theme';
import { availableThemes } from '../../config/themes';
import {
  SwatchIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface ThemeSelectorProps {
  className?: string;
  showLabels?: boolean;
}

const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  className = '',
  showLabels = true
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const currentTheme = useSelector((state: RootState) => state.theme.currentTheme);

  const handleThemeChange = (themeId: ThemeId) => {
    dispatch(setTheme(themeId));
  };

  const getThemePreview = (themeId: string) => {
    const theme = availableThemes.find(t => t.id === themeId);
    if (!theme) return {};

    switch (themeId) {
      case 'kfc':
        return {
          primary: '#dc2626',
          secondary: '#111827',
          accent: '#fbbf24',
        };
      case 'mcdonalds':
        return {
          primary: '#eab308',
          secondary: '#da1e37',
          accent: '#059669',
        };
      case 'subway':
        return {
          primary: '#00923f',
          secondary: '#ffdc00',
          accent: '#f97316',
        };
      default:
        return {
          primary: '#f97316',
          secondary: '#64748b',
          accent: '#10b981',
        };
    }
  };

  return (
    <div className={`theme-selector ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <SwatchIcon className="w-5 h-5 text-gray-600" />
        <h3 className="font-medium text-gray-900">Restaurant Theme</h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {availableThemes.map((theme) => {
          const colors = getThemePreview(theme.id);
          const isSelected = currentTheme === theme.id;

          return (
            <button
              key={theme.id}
              onClick={() => handleThemeChange(theme.id as ThemeId)}
              className={`relative p-3 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${
                isSelected
                  ? 'border-blue-500 shadow-lg'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Theme Color Preview */}
              <div className="flex flex-col items-center space-y-2">
                <div className="flex space-x-1">
                  <div
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: colors.primary }}
                  />
                  <div
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: colors.secondary }}
                  />
                  <div
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: colors.accent }}
                  />
                </div>

                {showLabels && (
                  <span className="text-xs font-medium text-gray-700 text-center">
                    {theme.name}
                  </span>
                )}
              </div>

              {/* Selected Indicator */}
              {isSelected && (
                <div className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full p-1">
                  <CheckIcon className="w-3 h-3" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Theme Description */}
      {showLabels && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            {currentTheme === 'kfc' && "KFC-inspired red and black color scheme with bold styling"}
            {currentTheme === 'mcdonalds' && "McDonald's golden arches yellow and red branding"}
            {currentTheme === 'subway' && "Subway's fresh green and yellow healthy theme"}
            {currentTheme === 'default' && "Classic restaurant theme with warm orange tones"}
          </p>
        </div>
      )}
    </div>
  );
};

export default ThemeSelector;